import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  const languages = [
    { code: 'en', name: t('language.en') },
    { code: 'zh', name: t('language.zh') },
    { code: 'ja', name: t('language.ja') },
    { code: 'ko', name: t('language.ko') },
    { code: 'fr', name: t('language.fr') },
    { code: 'de', name: t('language.de') },
    { code: 'es', name: t('language.es') },
    { code: 'it', name: t('language.it') },
    { code: 'pt', name: t('language.pt') },
    { code: 'ru', name: t('language.ru') },
    { code: 'ar', name: t('language.ar') },
    { code: 'hi', name: t('language.hi') }
  ];

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsLanguageOpen(false);
    if (window.trackLanguageChange) {
      window.trackLanguageChange(lng);
    }
  };

  const navItems = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.games'), href: '/#games' },
    { name: t('nav.about'), href: '/#about' },
    { name: t('nav.contact'), href: '/#contact' }
  ];

  return (
    <nav
      className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-md border-b"
      style={{
        borderColor: 'var(--border)',
        paddingTop: 'env(safe-area-inset-top, 0)'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <motion.a
            href="/"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2.5 min-h-[44px]"
          >
            <img src="/logo.svg" alt="" width={28} height={28} className="rounded-md" />
            <span className="text-lg sm:text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {t('nav.brand')}
            </span>
          </motion.a>

          <div className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              <motion.a
                key={item.name}
                href={item.href}
                className="transition-colors duration-300 font-medium min-h-[44px] inline-flex items-center"
                style={{ color: 'var(--text-secondary)' }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {item.name}
              </motion.a>
            ))}

            <div className="relative">
              <button
                type="button"
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="flex items-center gap-2 transition-colors duration-300 min-h-[44px] px-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                <Globe className="h-5 w-5" />
                <span>{languages.find((l) => l.code === i18n.language)?.name}</span>
              </button>

              {isLanguageOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute right-0 mt-2 w-44 shell-card py-1 max-h-80 overflow-y-auto"
                >
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => changeLanguage(lang.code)}
                      className="w-full text-left px-4 py-2.5 text-sm transition-colors duration-200 min-h-[44px]"
                      style={{
                        color: i18n.language === lang.code ? 'var(--accent)' : 'var(--text-secondary)',
                        background: i18n.language === lang.code ? 'var(--accent-soft)' : 'transparent'
                      }}
                    >
                      {lang.name}
                    </button>
                  ))}
                </motion.div>
              )}
            </div>
          </div>

          <div className="md:hidden">
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
              style={{ color: 'var(--accent)' }}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t pb-3"
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="pt-2 space-y-1">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="block px-3 py-3 rounded-lg font-medium min-h-[44px]"
                  style={{ color: 'var(--text-secondary)' }}
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </a>
              ))}

              <div className="px-3 py-2">
                <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--text-secondary)' }}>
                  <Globe className="h-5 w-5" />
                  <span>{t('language.selector')}</span>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => {
                        changeLanguage(lang.code);
                        setIsOpen(false);
                      }}
                      className="text-left px-3 py-2.5 text-sm rounded-lg min-h-[44px]"
                      style={{
                        color: i18n.language === lang.code ? 'var(--accent)' : 'var(--text-secondary)',
                        background: i18n.language === lang.code ? 'var(--accent-soft)' : 'var(--bg)'
                      }}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
